import React from 'react';
import { useIntl } from 'react-intl';
import { MdKeyboardDoubleArrowLeft, MdKeyboardDoubleArrowRight } from 'react-icons/md';
import { BUDDIES_PAIR, IconButton, PanelHeader, TOOLTIP_ALIGN_START, type BuddiesWho } from '@aibuild/ui';
import type { UserAppDraft } from '@api/appBuilder';
import AppDetailsForm from '../AppDetailsForm';
import { useAppBuilderActions } from '../useAppBuilder';
import CoverField from './CoverField';
import DetailsFilling from './DetailsFilling';
import { RailContent, RailStrip } from './DetailsRail.styled';

interface DetailsRailProps {
  readonly draft: UserAppDraft;
  readonly onReload: () => void;
  readonly open: boolean;
  /** Absent where the rail is a pane of its own and cannot fold. */
  readonly onToggle?: () => void;
  readonly building?: boolean;
  /** The first build is still writing the details, so they are not shown yet. */
  readonly filling?: boolean;
  readonly thumbnailBuddy?: BuddiesWho;
}

// Folded, the fields stay mounted so unsaved edits survive.
const DetailsRail: React.FC<DetailsRailProps> = ({
  draft,
  onReload,
  open,
  onToggle,
  building,
  filling = false,
  thumbnailBuddy = BUDDIES_PAIR,
}) => {
  const intl = useIntl();
  const { updateDraft, isSavingDraft } = useAppBuilderActions();
  const collapse = intl.formatMessage({ id: 'apps.builder.rail.collapse', defaultMessage: 'Hide details' });
  const expand = intl.formatMessage({ id: 'apps.builder.rail.expand', defaultMessage: 'Show details' });

  return (
    <>
      {onToggle && !open && (
        <RailStrip>
          <IconButton
            ariaLabel={expand}
            tooltip={expand}
            tooltipAlign={TOOLTIP_ALIGN_START}
            icon={<MdKeyboardDoubleArrowRight size={18} aria-hidden="true" />}
            size="sm"
            variant="ghost"
            aria-expanded={false}
            onClick={onToggle}
            dataTestId="app-builder__rail-expand"
          />
        </RailStrip>
      )}
      <RailContent hidden={!open}>
        <PanelHeader
          title={intl.formatMessage({ id: 'apps.builder.rail.title', defaultMessage: 'Details' })}
          actions={
            onToggle && (
              <IconButton
                ariaLabel={collapse}
                tooltip={collapse}
                icon={<MdKeyboardDoubleArrowLeft size={18} aria-hidden="true" />}
                size="sm"
                variant="ghost"
                aria-expanded
                onClick={onToggle}
                dataTestId="app-builder__rail-collapse"
              />
            )
          }
          bordered
          dataTestId="app-builder__rail-header"
        />
        {filling ? (
          <DetailsFilling buddy={thumbnailBuddy === BUDDIES_PAIR ? undefined : thumbnailBuddy} />
        ) : (
          <AppDetailsForm
            draft={draft}
            saving={isSavingDraft}
            onSave={(update) => updateDraft({ appKey: draft.appKey, update })}
            onReload={onReload}
            building={building}
            cover={<CoverField draft={draft} onReload={onReload} buddy={thumbnailBuddy} />}
          />
        )}
      </RailContent>
    </>
  );
};

export default DetailsRail;
